/** Shared player. Its media is the short Bitcoin Live intro; the post plays after it. */
export const SHARED_WISTIA_PLAYER_ID = "7twlrlom1r";

/**
 * Flatten Wistia Aurora chrome so no outer ring appears around the player.
 * Page CSS cannot restyle shadow-DOM internals; options + host styles cover it.
 */
export const POST_DETAIL_WISTIA_OPTIONS = {
  playerColor: "f1591e",
  videoFoam: true,
  playsinline: true,
  // Lovable provides the big circular play — hide Wistia's default.
  playButton: false,
  // Square fill inside our rounded frame (avoid double rounded ring).
  roundedPlayer: 0,
  playerBorderRadius: 0,
};

/** Apply square / no-border appearance on a ready Wistia API handle. */
export const flattenWistiaPlayerChrome = (video) => {
  if (!video) return;

  try {
    if (typeof video.roundedPlayer === "number" || "roundedPlayer" in video) {
      video.roundedPlayer = 0;
    }
  } catch (_) {
    /* ignore */
  }

  try {
    if (
      typeof video.playerBorderRadius === "number" ||
      "playerBorderRadius" in video
    ) {
      video.playerBorderRadius = 0;
    }
  } catch (_) {
    /* ignore */
  }

  const root =
    (typeof video.container === "function" && video.container()) ||
    video.container ||
    document.getElementById("wistia_player") ||
    document.querySelector(".post-detail-video wistia-player") ||
    document.querySelector(".post-detail-video .wistia_embed");

  if (!root || !(root instanceof Element)) return;

  root.style.border = "0";
  root.style.borderRadius = "0";
  root.style.boxShadow = "none";
  root.style.outline = "none";

  const host =
    root.tagName === "WISTIA-PLAYER"
      ? root
      : root.querySelector?.("wistia-player");
  if (host) {
    host.style.border = "0";
    host.style.borderRadius = "0";
    host.style.boxShadow = "none";
    host.style.outline = "none";
    try {
      host.roundedPlayer = 0;
      host.playerBorderRadius = 0;
      host.setAttribute("rounded-player", "0");
      host.setAttribute("player-border-radius", "0");
    } catch (_) {
      /* ignore */
    }
  }
};

let activePostMediaId = null;

/** Ignore a bind from a post the viewer already left. */
export const setActivePostMediaId = (mediaId) => {
  activePostMediaId = mediaId || null;
};

const findIntroPlayer = () =>
  window.Wistia?.api?.(SHARED_WISTIA_PLAYER_ID) ||
  window.Wistia?.api?.("wistia_player");

/**
 * Prod does only this: add the post onto the intro player.
 * Wistia plays 7twlrlom1r, then the queued media. No replaceWith, no play().
 */
const queuePost = (video, mediaId) => {
  if (!video || !mediaId || typeof video.addToPlaylist !== "function") {
    return false;
  }
  flattenWistiaPlayerChrome(video);
  if (video._blfPostId === mediaId) return true;
  video.addToPlaylist(mediaId);
  video._blfPostId = mediaId;
  return true;
};

export const releasePostDetailWistia = () => {
  const video = findIntroPlayer();
  try {
    video?.remove?.();
  } catch (_) {
    /* ignore */
  }
};

/** Same call production makes from Post/component.js after load. */
export const bindPostDetailWistia = (mediaId) => {
  if (!mediaId) return false;
  if (activePostMediaId && mediaId !== activePostMediaId) return false;

  const videoPlayer = findIntroPlayer();
  if (videoPlayer) {
    return queuePost(videoPlayer, mediaId);
  }

  if (window.Wistia?.embeds?.setup) {
    window.Wistia.embeds.setup();
  }

  return false;
};

/**
 * Our poster button stands in for Wistia's play button.
 * The playlist must already contain this post, or play() starts the intro
 * clip and never reaches the post.
 */
export const playPostDetailWistia = (mediaId) => {
  const tryPlay = () => {
    const video = findIntroPlayer();
    if (!video || typeof video.play !== "function") return false;
    if (mediaId && video._blfPostId !== mediaId) {
      queuePost(video, mediaId);
    }
    if (mediaId && video._blfPostId !== mediaId) return false;
    try {
      video.play();
      return true;
    } catch (_) {
      return false;
    }
  };

  if (tryPlay()) return true;
  window.setTimeout(tryPlay, 250);
  window.setTimeout(tryPlay, 700);
  return false;
};
